import React, { useCallback, useContext } from 'react';
import { useDispatch } from 'react-redux';
import ddt from '@wix/da-ddt';
import { MatureDeviationContext } from '../../Context/MatureDeviationContext';
import { normalizePremiumFolderData } from '../../utils/normalizePremiumFolderData';
import { useSubscriptionTier } from '../../utils/hooks/useSubscriptionTier';
import { overrideSubscribeMatureFilter } from '../../Tier/redux/consumer/actions';
import { DeviationTypes } from '../../types/deviation';
import { getDeviationType, getShouldDisplayPaywall } from '../_helpers';
import HiddenOverlay from '../HiddenOverlay';
import FolderPaywallFullView from './_partials/FolderPaywall';
import SubPaywall from './_partials/SubPaywall';
import ImageFullView from './Image';
import FilmFullView from './Film';
import JournalFullView from './Journal';
import LiteratureFullView from './Literature';
import PdfFullView from './Pdf';
import PollFullView from './Poll';
import StatusFullView from './Status';
import CommissionFullView from './Commission';
import TierFullView from './Tier';
import UnknownFullView from './Unknown';
import { FullViewProps } from './types';

const logger = ddt.createLogger('multifile');

const DeviationFullView: React.FC<FullViewProps> = props => {
  const dispatch = useDispatch();

  const { deviation } = props;
  const deviationType = getDeviationType(deviation);

  const { isSubscribedButBlocked, needsSubscription } =
    useSubscriptionTier(deviation);
  const { showBlocked, overrideMatureFilter } = useContext(
    MatureDeviationContext
  );

  const overrideMatureFilterWrapper = useCallback(() => {
    if (deviationType === DeviationTypes.TIER) {
      dispatch(overrideSubscribeMatureFilter(deviation));
    }
    overrideMatureFilter();
  }, [overrideMatureFilter, dispatch, deviation, deviationType]);

  if (showBlocked) {
    return (
      <HiddenOverlay
        overrideMatureFilter={overrideMatureFilterWrapper}
        {...props}
      />
    );
  }

  const { isPremiumLocked } = normalizePremiumFolderData(
    deviation?.premiumFolderData
  );
  if (isPremiumLocked && deviationType !== DeviationTypes.LITERATURE) {
    return <FolderPaywallFullView {...props} />;
  }

  const isBlocked = needsSubscription || isSubscribedButBlocked;
  if (isBlocked && getShouldDisplayPaywall(deviationType)) {
    return <SubPaywall {...props} showImage />;
  }

  // todo: remove when stable
  logger.log('DeviationFullView', deviation.title, deviation.type, {
    showBlocked,
    isPremiumLocked,
    isBlocked,
  });

  switch (deviationType) {
    case DeviationTypes.IMAGE:
      return <ImageFullView {...props} />;
    case DeviationTypes.FILM:
      return <FilmFullView {...props} />;
    case DeviationTypes.PDF:
      return <PdfFullView {...props} />;
    case DeviationTypes.LITERATURE:
      return <LiteratureFullView {...props} />;
    case DeviationTypes.JOURNAL:
      return <JournalFullView {...props} />;
    case DeviationTypes.STATUS:
      return <StatusFullView {...props} />;
    case DeviationTypes.POLL:
      return <PollFullView {...props} />;
    case DeviationTypes.COMMISSION:
      return <CommissionFullView {...props} />;
    case DeviationTypes.TIER:
      return <TierFullView {...props} />;
    default:
      return <UnknownFullView {...props} type={deviationType} />;
  }
};

export default DeviationFullView;
